// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { VariantDisplayTypeValues } from "@insite/client-framework/Types/ApiModels";
import ProductAttributes, { ProductAttributesStyles } from "@insite/content-library/Components/ProductAttributes";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductImageBadges from "@insite/content-library/Components/ProductImageBadges";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductSearchScoreData from "@insite/content-library/Components/ProductSearchScoreData";
import ProductTextBadges from "@insite/content-library/Components/ProductTextBadges";
import ProductListVariantSwatch from "@insite/content-library/Widgets/ProductList/ProductListVariantSwatch";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { xHasRole } from "@tps/App/Store/Selectors/GeneralSelectors";
import ProductDescription, { ProductDescriptionStyles } from "@tps/Overrides/Components/ProductDescription";
import { USER_ROLES } from "@tps/XDL/constants";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    showBrand: boolean;
    showTitle: boolean;
    showPartNumbers: boolean;
    showAvailability: boolean;
    showAttributes: boolean;
    showImageBadges?: boolean;
    maxImageBadges?: number;
    showTextBadges?: boolean;
    maxTextBadges?: number;
    // XNG-Change
    onClick?: (isNewPageWasOpen: boolean) => void;
    redirectToParent?: boolean;
    isPlp?: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    settingsCollection: getSettingsCollection(state),
    isEmployee: xHasRole(state, USER_ROLES.EMPLOYEE),
});

type Props = ReturnType<typeof mapStateToProps> & OwnProps & HasProductContext;

export interface ProductListProductInformationStyles {
    container?: GridContainerProps;
    brandStyles?: ProductBrandStyles;
    descriptionStyles?: ProductDescriptionStyles;
    brandGridItem?: GridItemProps;
    descriptionGridItem?: GridItemProps;
    partNumbersGridItem?: GridItemProps;
    swatchGridItem?: GridItemProps;
    imageBadgeGridItem?: GridItemProps;
    textBadgeGridItem?: GridItemProps;
    partNumbersStyles?: ProductPartNumbersStyles;
    availabilityGridItem?: GridItemProps;
    availabilityStyles?: ProductAvailabilityStyles;
    attributesGridItem?: GridItemProps;
    attributes?: ProductAttributesStyles;
    searchScoreDataGridItem?: GridItemProps;
    // XNG-Change
    boostScoreGridItem?: GridItemProps;
    ootbBoostScoreGridItem?: GridItemProps;
    boostScoreTypography?: TypographyPresentationProps;
}

export const productInformationStyles: ProductListProductInformationStyles = {
    container: {
        gap: 10,
        css: css`
            width: 100%;
        `,
    },
    descriptionStyles: {
        productDetailLink: {
            css: css`
                width: 100%;
            `,
            typographyProps: {
                size: 20,
            },
        },
    },
    brandGridItem: {
        width: 12,
    },
    descriptionGridItem: {
        width: 12,
    },
    partNumbersGridItem: {
        width: 12,
    },
    swatchGridItem: {
        width: 12,
    },
    imageBadgeGridItem: {
        width: 12,
    },
    textBadgeGridItem: {
        width: 12,
    },
    availabilityGridItem: {
        width: 12,
    },
    attributesGridItem: {
        width: 12,
    },
    searchScoreDataGridItem: {
        width: 12,
    },
};

const styles = productInformationStyles;

const ProductListProductInformation = ({
    productContext,
    settingsCollection,
    showBrand,
    showTitle,
    showPartNumbers,
    showAvailability,
    showAttributes,
    showImageBadges,
    maxImageBadges,
    showTextBadges,
    maxTextBadges,
    onClick,
    // Xng-Changes
    redirectToParent,
    isEmployee,
    isPlp,
}: Props) => {
    const { product } = productContext;
    if (!product) {
        return null;
    }
    const boostScore = product?.properties?.elasticSearchBoost;
    const productScore = product?.properties?.searchBoost;
    const totalScore = Number(boostScore) * Number(productScore);
    const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges, showImageBadges });

    let variantDisplayTrait = null;
    if (product.variantTraits && product.variantTraits.length > 0) {
        const swatchedViewList = [
            VariantDisplayTypeValues.SwatchDropdown,
            VariantDisplayTypeValues.SwatchList,
            VariantDisplayTypeValues.SwatchGrid,
        ];
        const sortedVaraintTraits = product.variantTraits.slice().sort((a, b) => a.sortOrder - b.sortOrder);
        variantDisplayTrait = sortedVaraintTraits.find(x => swatchedViewList.includes(x.displayType));
    }

    return (
        <GridContainer {...styles.container}>
            {product.brand && showBrand && (
                <GridItem {...styles.brandGridItem}>
                    <ProductBrand brand={product.brand} extendedStyles={styles.brandStyles} />
                </GridItem>
            )}
            {showTitle && (
                <GridItem {...styles.descriptionGridItem}>
                    <ProductDescription
                        product={productContext}
                        extendedStyles={styles.descriptionStyles}
                        // XNG-Change
                        {...(onClick && onClick)}
                        redirectToParent={redirectToParent}
                    />
                </GridItem>
            )}
            {/* XNG-Change: TPS-971 : Product Sort Boost & Search Boost Job */}
            {isEmployee && isPlp && (
                <GridItem {...styles.boostScoreGridItem}>
                    <Typography {...styles.boostScoreTypography}>
                        {translate("Boost Score")}: {boostScore}
                    </Typography>
                    <Typography {...styles.boostScoreTypography}>
                        {translate("Product Score")}: {productScore}
                    </Typography>
                    <Typography {...styles.boostScoreTypography}>
                        {translate("Total")}: {totalScore}
                    </Typography>
                </GridItem>
            )}

            {isPlp && (
                <GridItem {...styles.ootbBoostScoreGridItem}>
                    <ProductSearchScoreData />
                </GridItem>
            )}

            {showPartNumbers && (
                <GridItem {...styles.partNumbersGridItem}>
                    <ProductPartNumbers
                        productNumber={product.productNumber}
                        customerProductNumber={product.customerProductNumber}
                        manufacturerItem={product.manufacturerItem}
                        extendedStyles={styles.partNumbersStyles}
                    />
                </GridItem>
            )}
            {variantDisplayTrait && (
                <GridItem {...styles.swatchGridItem}>
                    <ProductListVariantSwatch product={productContext} variantTrait={variantDisplayTrait} />
                </GridItem>
            )}
            {showTextBadges && textBadges && (
                <GridItem {...styles.textBadgeGridItem}>
                    <ProductTextBadges badgeList={textBadges} maxBadges={maxTextBadges ?? 4} />{" "}
                </GridItem>
            )}
            {showImageBadges && imageBadges && (
                <GridItem {...styles.imageBadgeGridItem}>
                    <ProductImageBadges badgeList={imageBadges} maxBadges={maxImageBadges ?? 4} />
                </GridItem>
            )}
            {showAvailability && (
                <GridItem {...styles.availabilityGridItem}>
                    <ProductContextAvailability extendedStyles={styles.availabilityStyles} />
                </GridItem>
            )}
            {showAttributes && (
                <GridItem {...styles.attributesGridItem}>
                    <ProductAttributes maximumNumberAttributeTypes={3} extendedStyles={styles.attributes} />
                </GridItem>
            )}
        </GridContainer>
    );
};

export default connect(mapStateToProps)(withProductContext(ProductListProductInformation));
